/**
 * Admin Affiliates — Painel de gerenciamento de afiliados
 * Abas: Pendentes, Ativos, Ganhos & Comissões, Configurações
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { MetricCard, TabBar, Toggle as UiToggle, Modal, Input, FormGroup, TableSkeleton, EmptyState, Button, Select, Textarea } from '../components/ui';

import { T } from '../styles/tokens';
const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 24 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 16 } as any,
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.whiteAlpha8}`, marginBottom: 24, overflowX: 'auto' as any } as any,
  tab: (active: boolean) => ({
    padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
    color: active ? T.primary : T.textSecondary, borderBottom: active ? `2px solid ${T.primary}` : '2px solid transparent',
    whiteSpace: 'nowrap' as any, transition: 'all 0.15s',
  }),
  input: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
  textarea: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', minHeight: 60, resize: 'vertical' as any, boxSizing: 'border-box' as any, fontFamily: 'inherit' } as any,
  btn: (variant: 'primary' | 'danger' | 'ghost' | 'success') => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
    ...(variant === 'primary' ? { background: T.primary, color: T.text } :
       variant === 'danger' ? { background: T.danger, color: T.text } :
       variant === 'success' ? { background: T.success, color: T.text } :
       { background: T.border, color: T.textSecondary }),
  }),
  table: { width: '100%', borderCollapse: 'collapse' as any, fontSize: 13 } as any,
  th: { textAlign: 'left' as any, padding: '10px 12px', color: T.textSecondary, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as any, letterSpacing: '0.5px', borderBottom: `1px solid ${T.border}` } as any,
  td: { padding: '12px 12px', color: T.text, borderBottom: `1px solid ${T.borderSubtle}` } as any,
  badge: (color: string) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${color}20`, color: color,
  }),
  label: { fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 4, display: 'block' } as any,
  formGroup: { marginBottom: 16 } as any,
  metricCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 20px', textAlign: 'center' as any } as any,
  overlay: { position: 'fixed' as any, inset: 0, background: T.bgOverlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 16, padding: 24, width: 420, maxWidth: '90vw' } as any,
  sectionTitle: { fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 } as any,
  sectionDesc: { fontSize: 12, color: T.textMuted, marginBottom: 16 } as any,
  select: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
};

// Toggle switch — usa componente ui/Toggle importado como UiToggle
const Toggle = ({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) => (
  <div style={{ marginBottom: 12 }}>
    <UiToggle label={label} value={value} onChange={onChange} size="sm" />
  </div>
);

// Skeleton shimmer
const SkeletonBlock = () => (
  <div style={{ ...s.card }}>
    {[1,2,3].map(i => (
      <div key={i} style={{ height: 14, background: T.border, borderRadius: 6, marginBottom: 12, width: `${60 + i * 10}%`, animation: 'pulse 1.5s ease-in-out infinite' }} />
    ))}
    <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
  </div>
);

// Multi-select com chips
const MultiSelect = ({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: string[]; label: string }) => {
  const selected = value === '*' ? options : value.split(',').map(s => s.trim()).filter(Boolean);
  const allSelected = selected.length === options.length || value === '*';
  const toggle = (opt: string) => {
    if (allSelected) { onChange(opt); return; }
    const newSel = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    onChange(newSel.length === options.length ? '*' : newSel.join(','));
  };
  return (
    <div style={s.formGroup}>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap' as any, gap: 6, marginTop: 4 }}>
        <div onClick={() => onChange(allSelected ? '' : '*')} style={{
          padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600,
          background: allSelected ? `${T.primary}20` : T.bgCardHover, color: allSelected ? T.primary : T.textMuted,
          border: `1px solid ${allSelected ? `${T.primary}40` : T.bgHover}`,
        }}>Todos</div>
        {options.map(opt => {
          const active = !allSelected && selected.includes(opt);
          return (
            <div key={opt} onClick={() => toggle(opt)} style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              background: active ? `${T.primary}20` : T.bgCardHover, color: active ? T.primary : T.textSecondary,
              border: `1px solid ${active ? `${T.primary}40` : T.bgHover}`,
            }}>{opt}</div>
          );
        })}
      </div>
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = { pending: T.warning, approved: T.success, rejected: T.danger, suspended: T.textMuted };
const STATUS_LABELS: Record<string, string> = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado', suspended: 'Suspenso' };

const TABS = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'active', label: 'Ativos' },
  { id: 'earnings', label: 'Ganhos & Comissões' },
  { id: 'settings', label: 'Configurações' },
];

const TRAFFIC_SOURCES = ['SEO', 'PPC', 'Email', 'Social Media', 'YouTube', 'Blog', 'Podcast', 'Direct'];
const PROMOTION_METHODS = ['SEO', 'PPC', 'Email Marketing', 'Social Media', 'Content Marketing', 'Video', 'Webinars', 'Coupon Sites'];

export function AdminAffiliates() {
  const [tab, setTab] = useState('pending');
  const [affiliates, setAffiliates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<any>(null);

  // Configurações
  const [settings, setSettings] = useState<any>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Modais
  const [approveModal, setApproveModal] = useState<any>(null);
  const [rejectModal, setRejectModal] = useState<any>(null);
  const [editModal, setEditModal] = useState<any>(null);
  const [approveForm, setApproveForm] = useState({ commission_llm_percent: 10, commission_subscription_percent: 10 });
  const [rejectReason, setRejectReason] = useState('');

  const fetchAffiliates = async (status?: string) => {
    try {
      setLoading(true);
      const st = status || (tab === 'pending' ? 'pending' : tab === 'active' ? 'approved' : 'all');
      const data = await api.get(`/admin/affiliates?status=${st}&limit=50`) as any;
      setAffiliates(data?.affiliates || []);
      setStats(data?.stats || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchEarnings = async () => {
    try {
      const data = await api.get('/admin/affiliates/earnings') as any;
      if (data) setEarnings(data);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const res = await api.get('/admin/affiliates/settings') as any;
      // api.get() já retorna data.data (unwrapped) — não usar res.json()
      if (res?.settings) setSettings(res.settings);
    } catch {} finally { setSettingsLoading(false); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setSaveMsg('');
      await api.put('/admin/affiliates/settings', settings);
      setSaveMsg('Salvo com sucesso ✓');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch { setSaveMsg('Erro ao salvar'); } finally { setSaving(false); }
  };

  const fetchDetail = async (id: string) => {
    try {
      const data = await api.get(`/admin/affiliates/${id}`) as any;
      if (data) setExpandedData(data);
    } catch {}
  };

  useEffect(() => {
    if (tab === 'earnings') fetchEarnings();
    else if (tab === 'settings') fetchSettings();
    else fetchAffiliates();
  }, [tab]);

  const handleApprove = async () => {
    if (!approveModal) return;
    try {
      await api.post(`/admin/affiliates/${approveModal.id}/approve`, approveForm);
      setApproveModal(null);
      fetchAffiliates();
    } catch {}
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await api.post(`/admin/affiliates/${rejectModal.id}/reject`, { reason: rejectReason });
      setRejectModal(null);
      setRejectReason('');
      fetchAffiliates();
    } catch {}
  };

  const handleSuspend = async (id: string) => {
    try {
      await api.post(`/admin/affiliates/${id}/suspend`, {});
      fetchAffiliates();
    } catch {}
  };

  const handleEditSave = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/affiliates/${editModal.id}`, {
        commission_llm_percent: editModal.commission_llm_percent,
        commission_subscription_percent: editModal.commission_subscription_percent,
      });
      setEditModal(null);
      fetchAffiliates();
    } catch {}
  };

  const toggleExpand = (id: string) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    setExpandedId(id);
    fetchDetail(id);
  };

  const getStatCount = (status: string) => {
    const found = stats.find((s: any) => s.status === status);
    return found ? (found as any).count : 0;
  };

  const fmt = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';

  // Helper pra atualizar campo de settings
  const upd = (field: string, value: any) => setSettings((prev: any) => ({ ...prev, [field]: value }));

  return (
    <div style={s.page}>
      <h1 style={s.title}>Painel de Afiliados</h1>

      {/* Métricas rápidas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <MetricCard value={getStatCount('pending')} label="Pendentes" color={T.warning} />
        <MetricCard value={getStatCount('approved')} label="Aprovados" color={T.success} />
        <MetricCard value={getStatCount('rejected')} label="Rejeitados" color={T.danger} />
        <MetricCard value={getStatCount('suspended')} label="Suspensos" color={T.textMuted} />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ── PENDENTES ── */}
      {tab === 'pending' && (
        <div>
          {loading ? <TableSkeleton /> :
           affiliates.length === 0 ? <div style={{ ...s.card, textAlign: 'center', color: T.textMuted }}>Nenhuma solicitação pendente</div> : (
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Nome</th><th style={s.th}>Email</th><th style={s.th}>Estratégia</th>
                <th style={s.th}>Canais</th><th style={s.th}>Data</th><th style={s.th}>Ações</th>
              </tr></thead>
              <tbody>
                {affiliates.map((a: any) => (
                  <tr key={a.id}>
                    <td style={s.td}>{a.full_name || a.customer_name || '-'}</td>
                    <td style={s.td}><span style={{ fontSize: 12 }}>{a.customer_email || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 12, maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.promotion_strategy || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 12 }}>{a.promotion_channels || '-'}</span></td>
                    <td style={s.td}>{fmt(a.created_at)}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button variant="success" size="sm" onClick={() => { setApproveModal(a); setApproveForm({ commission_llm_percent: 10, commission_subscription_percent: 10 }); }}>Aprovar</Button>
                        <Button variant="danger" size="sm" onClick={() => { setRejectModal(a); setRejectReason(''); }}>Rejeitar</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ATIVOS ── */}
      {tab === 'active' && (
        <div>
          {loading ? <TableSkeleton /> :
           affiliates.length === 0 ? <div style={{ ...s.card, textAlign: 'center', color: T.textMuted }}>Nenhum afiliado ativo</div> : (
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Nome</th><th style={s.th}>Email</th><th style={s.th}>LLM%</th><th style={s.th}>Sub%</th>
                <th style={s.th}>Referrals</th><th style={s.th}>Ganhos</th><th style={s.th}>Status</th><th style={s.th}>Ações</th>
              </tr></thead>
              <tbody>
                {affiliates.map((a: any) => (
                  <>
                    <tr key={a.id} onClick={() => toggleExpand(a.id)} style={{ cursor: 'pointer' }}>
                      <td style={s.td}>{a.full_name || a.customer_name || '-'}</td>
                      <td style={s.td}><span style={{ fontSize: 12 }}>{a.customer_email || '-'}</span></td>
                      <td style={s.td}>{a.commission_llm_percent}%</td>
                      <td style={s.td}>{a.commission_subscription_percent}%</td>
                      <td style={s.td}>{a.total_referrals || 0}</td>
                      <td style={s.td}>${((a.total_earnings_llm || 0) + (a.total_earnings_subscription || 0)).toFixed(2)}</td>
                      <td style={s.td}><span style={s.badge(STATUS_COLORS[a.status] || T.textMuted)}>{STATUS_LABELS[a.status] || a.status}</span></td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button variant="ghost" size="xs" onClick={(e: any) => { e.stopPropagation(); setEditModal({ ...a }); }}>✏️</Button>
                          <Button variant={a.status === 'suspended' ? 'success' : 'danger'} size="xs" onClick={(e: any) => { e.stopPropagation(); handleSuspend(a.id); }}>
                            {a.status === 'suspended' ? '▶️' : '⏸️'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === a.id && expandedData && (
                      <tr key={a.id + '-detail'}>
                        <td colSpan={8} style={{ padding: 16, background: T.bgSubtle }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Referrals ({expandedData.referrals?.length || 0})</div>
                              {(expandedData.referrals || []).slice(0, 5).map((r: any) => (
                                <div key={r.id} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                                  {r.customer_email || '***'} — {fmt(r.signup_at)} — ${r.total_spent?.toFixed(2) || '0.00'}
                                </div>
                              ))}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Últimos ganhos</div>
                              {(expandedData.earnings || []).slice(0, 5).map((e: any) => (
                                <div key={e.id} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                                  {e.source} — ${e.commission_amount?.toFixed(2)} ({e.commission_percent}%) — {fmt(e.created_at)}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ marginTop: 12, fontSize: 12, color: T.textMuted }}>
                            <b>Estratégia:</b> {expandedData.affiliate?.promotion_strategy || '-'}<br />
                            <b>Canais:</b> {expandedData.affiliate?.promotion_channels || '-'}<br />
                            <b>Notas:</b> {expandedData.affiliate?.application_notes || '-'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── GANHOS & COMISSÕES ── */}
      {tab === 'earnings' && earnings && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
            <div style={s.metricCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.success }}>${earnings.totals?.total_paid?.toFixed(2) || '0.00'}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Total Pago</div>
            </div>
            <div style={s.metricCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.warning }}>${earnings.totals?.total_pending?.toFixed(2) || '0.00'}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Total Pendente</div>
            </div>
            <div style={s.metricCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.primary }}>{earnings.totals?.total_affiliates || 0}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Afiliados Ativos</div>
            </div>
            <div style={s.metricCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.purple }}>{earnings.total_referrals || 0}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Total Referrals</div>
            </div>
          </div>

          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Afiliado</th><th style={s.th}>Email</th><th style={s.th}>Ganhos LLM</th>
              <th style={s.th}>Ganhos Sub</th><th style={s.th}>Total</th><th style={s.th}>Referrals</th>
            </tr></thead>
            <tbody>
              {(earnings.by_affiliate || []).map((a: any) => (
                <tr key={a.id}>
                  <td style={s.td}>{a.full_name || '-'}</td>
                  <td style={s.td}><span style={{ fontSize: 12 }}>{a.customer_email || '-'}</span></td>
                  <td style={s.td}>${a.earnings_llm?.toFixed(2) || '0.00'}</td>
                  <td style={s.td}>${a.earnings_sub?.toFixed(2) || '0.00'}</td>
                  <td style={s.td}><b>${a.earnings_total?.toFixed(2) || '0.00'}</b></td>
                  <td style={s.td}>{a.referral_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── CONFIGURAÇÕES ── */}
      {tab === 'settings' && (
        <div>
          {settingsLoading || !settings ? (
            <>{[1,2,3].map(i => <TableSkeleton key={i} rows={3} />)}</>
          ) : (
            <>
              {/* Seção 1 — Geral */}
              <div style={s.card}>
                <div style={s.sectionTitle}>⚙️ Geral</div>
                <div style={s.sectionDesc}>Controles gerais do programa de afiliados</div>

                {/* Toggle grande pra programa ativo */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, padding: '12px 16px', background: settings.affiliate_system_enabled ? T.successBg : T.dangerBg, borderRadius: 10, border: `1px solid ${settings.affiliate_system_enabled ? T.successBg : T.dangerBg}` }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: settings.affiliate_system_enabled ? T.success : T.danger }}>
                      {settings.affiliate_system_enabled ? '🟢 Programa Ativo' : '🔴 Programa Inativo'}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Ativar/desativar todo o sistema de afiliados</div>
                  </div>
                  <div onClick={() => upd('affiliate_system_enabled', settings.affiliate_system_enabled ? 0 : 1)} style={{
                    width: 56, height: 30, borderRadius: 15, cursor: 'pointer', position: 'relative' as any,
                    background: settings.affiliate_system_enabled ? T.success : T.borderHover, transition: 'background 0.2s',
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', background: '#fff', position: 'absolute' as any,
                      top: 3, left: settings.affiliate_system_enabled ? 29 : 3, transition: 'left 0.2s', boxShadow: `0 1px 3px ${T.blackAlpha30}`,
                    }} />
                  </div>
                </div>

                <Toggle label="Aprovação manual necessária" value={!!settings.require_approval} onChange={v => upd('require_approval', v ? 1 : 0)} />
                <Toggle label="Auto-referral permitido (afiliado ganha da própria compra)" value={!!settings.self_referral_allowed} onChange={v => upd('self_referral_allowed', v ? 1 : 0)} />
                <div style={s.formGroup}>
                  <Input label="Máximo de afiliados ativos (0 = ilimitado)" type="number" value={String(settings.max_active_affiliates ?? 0)}
                    onChange={(v) => upd('max_active_affiliates', parseInt(v) || 0)} />
                </div>
              </div>

              {/* Seção 2 — Comissões */}
              <div style={s.card}>
                <div style={s.sectionTitle}>💰 Comissões Padrão</div>
                <div style={s.sectionDesc}>Percentuais de comissão padrão para novos afiliados</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={s.formGroup}>
                    <Input label="Comissão LLM Créditos (%)" type="number" value={String(settings.default_commission_llm_percent ?? 10)}
                      onChange={(v) => upd('default_commission_llm_percent', parseFloat(v) || 0)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Comissão Subscription (%)" type="number" value={String(settings.default_commission_subscription_percent ?? 10)}
                      onChange={(v) => upd('default_commission_subscription_percent', parseFloat(v) || 0)} />
                  </div>
                </div>
                <Toggle label="Comissão recorrente em renewals" value={!!settings.recurring_commissions} onChange={v => upd('recurring_commissions', v ? 1 : 0)} />
                <div style={s.formGroup}>
                  <Input label="Período de lock (dias) — anti-chargeback antes de confirmar comissão" type="number" value={String(settings.commission_lock_days ?? 30)}
                    onChange={(v) => upd('commission_lock_days', parseInt(v) || 0)} />
                </div>
              </div>

              {/* Seção 3 — Cookie & Tracking */}
              <div style={s.card}>
                <div style={s.sectionTitle}>🍪 Cookie & Tracking</div>
                <div style={s.sectionDesc}>Configurações de rastreamento de referral</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={s.formGroup}>
                    <Input label="Duração do cookie (dias)" type="number" value={String(settings.cookie_duration_days ?? 30)}
                      onChange={(v) => upd('cookie_duration_days', parseInt(v) || 30)} />
                  </div>
                  <div style={s.formGroup}>
                    <Select label="Modelo de atribuição" value={settings.attribution_model || 'last_click'}
                      onChange={(v) => upd('attribution_model', v)}
                      options={[{ value: 'first_click', label: 'First Click' }, { value: 'last_click', label: 'Last Click' }, { value: 'linear', label: 'Linear' }]} />
                  </div>
                </div>
              </div>

              {/* Seção 4 — Pagamentos */}
              <div style={s.card}>
                <div style={s.sectionTitle}>💳 Pagamentos</div>
                <div style={s.sectionDesc}>Regras de saque e frequência de pagamento</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={s.formGroup}>
                    <Input label="Valor mínimo para saque ($)" type="number" value={String(settings.minimum_payout ?? 50)}
                      onChange={(v) => upd('minimum_payout', parseFloat(v) || 0)} />
                  </div>
                  <div style={s.formGroup}>
                    <Select label="Frequência de pagamento" value={settings.payout_frequency || 'monthly'}
                      onChange={(v) => upd('payout_frequency', v)}
                      options={[{ value: 'weekly', label: 'Semanal' }, { value: 'biweekly', label: 'Quinzenal' }, { value: 'monthly', label: 'Mensal' }]} />
                  </div>
                </div>
              </div>

              {/* Seção 5 — Limites de Campanhas */}
              <div style={s.card}>
                <div style={s.sectionTitle}>📊 Limites de Campanhas</div>
                <div style={s.sectionDesc}>Limites máximos que afiliados podem oferecer em campanhas</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={s.formGroup}>
                    <Input label="Máx créditos por campanha ($)" type="number" value={String(settings.max_credits_amount ?? 10)}
                      onChange={(v) => upd('max_credits_amount', parseFloat(v) || 0)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Máx dias trial" type="number" value={String(settings.max_trial_days ?? 14)}
                      onChange={(v) => upd('max_trial_days', parseInt(v) || 0)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Plano máximo trial" value={settings.max_trial_plan || 'pro'}
                      onChange={(v) => upd('max_trial_plan', v)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Máx desconto (%)" type="number" value={String(settings.max_discount_percent ?? 50)}
                      onChange={(v) => upd('max_discount_percent', parseFloat(v) || 0)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Plano máximo desconto" value={settings.max_discount_plan || 'pro'}
                      onChange={(v) => upd('max_discount_plan', v)} />
                  </div>
                  <div style={s.formGroup}>
                    <Input label="Máx resgates por campanha" type="number" value={String(settings.max_redemptions_per_campaign ?? 100)}
                      onChange={(v) => upd('max_redemptions_per_campaign', parseInt(v) || 0)} />
                  </div>
                </div>
              </div>

              {/* Seção 6 — Tráfego & Promoção */}
              <div style={s.card}>
                <div style={s.sectionTitle}>🌐 Tráfego & Promoção</div>
                <div style={s.sectionDesc}>Fontes de tráfego e métodos de promoção permitidos</div>
                <MultiSelect label="Fontes de tráfego permitidas" options={TRAFFIC_SOURCES}
                  value={settings.allowed_traffic_sources || '*'} onChange={v => upd('allowed_traffic_sources', v)} />
                <MultiSelect label="Métodos de promoção permitidos" options={PROMOTION_METHODS}
                  value={settings.allowed_promotion_methods || '*'} onChange={v => upd('allowed_promotion_methods', v)} />
                <div style={s.formGroup}>
                  <label style={s.label}>Keywords banidas / Regras de brand bidding</label>
                  <Textarea style={s.textarea} value={settings.banned_keywords || ''}
                    onInput={(e) => upd('banned_keywords', (e.target as HTMLTextAreaElement).value)}
                    placeholder="Uma keyword por linha ou separadas por vírgula..." />
                </div>
              </div>

              {/* Seção 7 — Termos & Comunicação */}
              <div style={s.card}>
                <div style={s.sectionTitle}>📝 Termos & Comunicação</div>
                <div style={s.sectionDesc}>Termos do programa e mensagem de boas-vindas pós-aprovação</div>
                <div style={s.formGroup}>
                  <label style={s.label}>Termos e condições do programa</label>
                  <Textarea style={{ ...s.textarea, minHeight: 100 }} value={settings.terms_and_conditions || ''}
                    onInput={(e) => upd('terms_and_conditions', (e.target as HTMLTextAreaElement).value)}
                    placeholder="Escreva os termos e condições do programa de afiliados..." />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Mensagem de boas-vindas (pós-aprovação)</label>
                  <Textarea style={{ ...s.textarea, minHeight: 80 }} value={settings.welcome_message || ''}
                    onInput={(e) => upd('welcome_message', (e.target as HTMLTextAreaElement).value)}
                    placeholder="Mensagem enviada ao afiliado após aprovação..." />
                </div>
              </div>

              {/* Botão Salvar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, marginBottom: 32 }}>
                <Button variant="primary" size="lg" onClick={saveSettings} disabled={saving} loading={saving}>
                  {saving ? 'Salvando...' : '💾 Salvar Configurações'}
                </Button>
                {saveMsg && <span style={{ fontSize: 13, color: saveMsg.includes('sucesso') ? T.success : T.danger, fontWeight: 600 }}>{saveMsg}</span>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Modal Aprovar ── */}
      <Modal open={!!approveModal} onClose={() => setApproveModal(null)} title="Aprovar Afiliado" footer={
        <>
          <Button variant="ghost" onClick={() => setApproveModal(null)}>Cancelar</Button>
          <Button variant="success" onClick={handleApprove}>Confirmar Aprovação</Button>
        </>
      }>
        {approveModal && <>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>{approveModal.full_name || approveModal.customer_email}</p>
          <FormGroup label="Comissão LLM (%)">
            <Input type="number" value={approveForm.commission_llm_percent}
              onChange={(v) => setApproveForm(p => ({ ...p, commission_llm_percent: parseFloat(v) || 0 }))} />
          </FormGroup>
          <FormGroup label="Comissão Subscription (%)">
            <Input type="number" value={approveForm.commission_subscription_percent}
              onChange={(v) => setApproveForm(p => ({ ...p, commission_subscription_percent: parseFloat(v) || 0 }))} />
          </FormGroup>
        </>}
      </Modal>

      {/* ── Modal Rejeitar ── */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Rejeitar Afiliado" footer={
        <>
          <Button variant="ghost" onClick={() => setRejectModal(null)}>Cancelar</Button>
          <Button variant="danger" onClick={handleReject}>Confirmar Rejeição</Button>
        </>
      }>
        {rejectModal && <>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>{rejectModal.full_name || rejectModal.customer_email}</p>
          <FormGroup label="Motivo da Rejeição">
            <Textarea style={s.textarea} value={rejectReason} onInput={(e) => setRejectReason((e.target as HTMLTextAreaElement).value)} placeholder="Descreva o motivo..." />
          </FormGroup>
        </>}
      </Modal>

      {/* ── Modal Editar ── */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title="✏️ Editar Comissão" footer={
        <>
          <Button variant="ghost" onClick={() => setEditModal(null)}>Cancelar</Button>
          <Button variant="primary" onClick={handleEditSave}>Salvar</Button>
        </>
      }>
        {editModal && <>
          <FormGroup label="Comissão LLM (%)">
            <Input type="number" value={editModal.commission_llm_percent}
              onChange={(v) => setEditModal((p: any) => ({ ...p, commission_llm_percent: parseFloat(v) || 0 }))} />
          </FormGroup>
          <FormGroup label="Comissão Subscription (%)">
            <Input type="number" value={editModal.commission_subscription_percent}
              onChange={(v) => setEditModal((p: any) => ({ ...p, commission_subscription_percent: parseFloat(v) || 0 }))} />
          </FormGroup>
        </>}
      </Modal>
    </div>
  );
}
